import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Save, MailCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export const useCounsellors = () =>
  useQuery({ queryKey: ["counsellors"], queryFn: () => api.get("/counsellors").then((r) => r.data.items) });

const Preview = () => {
  const { data: pv } = useQuery({ queryKey: ["reminders-preview"], queryFn: () => api.get("/reminders/preview").then((r) => r.data) });
  const { data: last } = useQuery({ queryKey: ["reminders-last"], queryFn: () => api.get("/reminders/last").then((r) => r.data) });
  if (!pv) return null;
  return (
    <div className="mt-5 space-y-3 border-t border-slate-100 pt-4 text-sm" data-testid="reminders-preview">
      <div className="text-xs uppercase tracking-wider text-slate-500">Today's reminder preview</div>
      {pv.counsellors.map((c) => (
        <div key={`${c.name}-${c.email}`} className="flex justify-between" data-testid={`reminder-preview-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
          <span>{c.name} <span className="text-slate-500">· {c.email}</span></span>
          <span className="font-num">{c.items.length} call-back{c.items.length === 1 ? "" : "s"}{c.thanks?.length ? ` · ${c.thanks.length} to thank` : ""}{c.spot?.length ? ` · ${c.spot.length} Spot ending` : ""}{c.missing?.length ? ` · ${c.missing.length} to record` : ""}</span>
        </div>
      ))}
      {pv.unmatched.length > 0 && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800" data-testid="reminders-unmatched">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Due follow-ups exist for counsellors without an email here: {pv.unmatched.join(", ")}
        </div>
      )}
      {last && (
        <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="reminders-last-run">
          <MailCheck className="h-3.5 w-3.5 text-plum-700" />
          Last run {new Date(last.finished_at).toLocaleString("en-IN")} · {last.sent.filter((s) => s.ok).length} sent
          {last.sent.some((s) => !s.ok) && <span className="text-red-600"> · {last.sent.filter((s) => !s.ok).length} failed</span>}
        </div>
      )}
    </div>
  );
};

export const CounsellorsCard = () => {
  const qc = useQueryClient();
  const { data } = useCounsellors();
  const [items, setItems] = useState([]);
  useEffect(() => { if (data) setItems(data); }, [data]);

  const set = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    const clean = items.map((x) => ({ name: x.name.trim(), email: x.email.trim() })).filter((x) => x.name || x.email);
    if (clean.some((x) => !x.name || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x.email))) return toast.error("Each counsellor needs a name and a valid email");
    try {
      const r = await api.put("/counsellors", { items: clean });
      qc.setQueryData(["counsellors"], r.data.items);
      setItems(r.data.items);
      qc.invalidateQueries({ queryKey: ["reminders-preview"] });
      toast.success("Counsellors saved");
    } catch { toast.error("Could not save counsellors"); }
  };

  return (
    <div className="fade-up d2 mt-6 rounded-lg border border-plum-900/10 bg-white p-6" data-testid="counsellors-card">
      <h2 className="font-display text-base font-bold text-plum-950">Counsellors & Call Reminders</h2>
      <p className="mt-1 text-xs text-slate-500">Every day at 9:00 AM IST each counsellor gets an email listing parents to call back (due today + overdue). Names must match the "Adm Counsellor Name" on receipts.</p>
      <div className="mt-4 space-y-2">
        {items.map((c, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.3fr_auto] gap-2">
            <Input placeholder="Name" value={c.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`counsellor-name-${i}`} />
            <Input placeholder="Email" type="email" value={c.email} onChange={(e) => set(i, "email", e.target.value)} data-testid={`counsellor-email-${i}`} />
            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`counsellor-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" onClick={() => setItems([...items, { name: "", email: "" }])} data-testid="counsellor-add-btn"><Plus className="mr-2 h-4 w-4" />Add counsellor</Button>
        <Button onClick={save} className="bg-plum-700 hover:bg-plum-800" data-testid="counsellors-save-btn"><Save className="mr-2 h-4 w-4" />Save</Button>
      </div>
      <Preview />
    </div>
  );
};
